import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { useSpeakingMap } from '@/lib/voiceLevels';
import { UserCardTrigger } from '@/components/userCard/UserCardTrigger';
import { MicOffIcon, HeadphonesOffIcon, ScreenShareIcon } from '@/components/icons';
import type { GuildMemberRow, VoiceUserState } from '@/types/api';
import { micShownOff } from '@/lib/voiceIndicators';

/** Live mm:ss timer for an active voice call, ticking once a second. */
export function VoiceCallTimer({ startedAt, className }: { startedAt: number; className?: string }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  const secs = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return <span className={className}>{`${m}:${s.toString().padStart(2, '0')}`}</span>;
}

/** One participant row under a voice channel in the sidebar: avatar (with a
 *  green speaking ring), name, and mute/deafen/screen-share status icons. */
export function VoiceParticipant(props: {
  state: VoiceUserState;
  member: GuildMemberRow | null;
  guildId: string;
  channelId: string;
}) {
  const { t } = useTranslation();
  const { state, member } = props;
  const myId = useAuthStore((s) => s.user?.id ?? null);
  const speakingMap = useSpeakingMap();
  const name = member?.nickname || member?.user.displayName || member?.user.username || t('channels.memberFallback');
  const avatarUrl = member?.user.avatarUrl ?? null;
  const muted = micShownOff(state);
  const deafened = state.selfDeaf || state.serverDeaf;
  // Live speaking signal — `local` is the synthetic key for our own mic.
  const speakingKey = state.userId === myId ? 'local' : state.userId;
  const speaking = !muted && (speakingMap.get(speakingKey) ?? false);

  const inner = (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <span
        className="grid size-6 shrink-0 place-items-center overflow-hidden rounded-lg bg-default/60 text-[11px] font-medium text-foreground transition-shadow"
        // Green ring + glow while talking — matches the voice-room tiles and
        // reads on both light and dark themes (Discord-style cue).
        style={
          speaking
            ? { boxShadow: '0 0 0 2px rgb(var(--bk-success)), 0 0 8px 0 rgb(var(--bk-success) / 0.6)' }
            : undefined
        }
      >
        <IdentityAvatar id={state.userId} name={name} src={avatarUrl} size="xs" />
      </span>
      <span
        className={
          'truncate text-[13px] ' +
          (speaking ? 'text-success' : deafened || muted ? 'text-muted' : 'text-foreground/90')
        }
      >
        {name}
      </span>
    </span>
  );

  return (
    <li className="flex items-center gap-1 rounded-lg px-2 py-0.5 transition-colors hover:bg-default/30 [@media(pointer:coarse)]:min-h-11">
      {member ? (
        <UserCardTrigger
          user={member.user}
          guildId={props.guildId}
          voice={{ channelId: props.channelId }}
          label={t('ui.member.profileOf', { name })}
          className="flex min-w-0 flex-1 items-center [@media(pointer:coarse)]:min-h-11"
        >
          {inner}
        </UserCardTrigger>
      ) : (
        inner
      )}
      <span className="flex shrink-0 items-center gap-1 text-muted">
        {/* A glyph, not a text chip. 9px bold Latin in a participant row was
            unreadable at the size it was given and shouted louder than the
            person's own name. Accent, not danger: the two icons beside this
            one are red because something is OFF — a muted mic, killed audio.
            Sharing a screen is a feature in use, and painting it the same red
            made "I'm streaming" and "my mic is broken" one alarm. Same 14px as
            its siblings so the row keeps one rhythm; the slow pulse carries
            "right now" without a word. */}
        {state.screenOn && (
          <ScreenShareIcon
            role="img"
            aria-label={t('voice.sharing')}
            className="size-3.5 text-accent"
            style={{ animation: 'bk-live-dot 1.8s ease-in-out infinite' }}
          />
        )}
        {deafened && (
          <HeadphonesOffIcon
            role="img"
            aria-label={state.serverDeaf ? t('userWidget.deafServerMuted') : t('voice.deafened')}
            className="size-3.5 text-danger"
          />
        )}
        {muted && (
          <MicOffIcon
            role="img"
            aria-label={state.serverMute ? t('userWidget.micServerMuted') : t('voice.muted')}
            className="size-3.5 text-danger"
          />
        )}
      </span>
    </li>
  );
}
